import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { SettingsSection, SettingsList, SettingsListItem } from './ui';
import {
  sharingApi,
  BudgetShareOwned,
  BudgetShareReceived,
} from '../../services/budgetApi';
import styles from './BudgetShareSettings.module.css';

export interface BudgetShareSettingsProps {
  /**
   * When rendered inside a larger Settings screen, we avoid duplicated page
   * container padding and titles.
   */
  embedded?: boolean;
}

export const BudgetShareSettings: React.FC<BudgetShareSettingsProps> = ({ embedded = false }) => {
  const [sharesOwned, setSharesOwned] = useState<BudgetShareOwned[]>([]);
  const [sharesReceived, setSharesReceived] = useState<BudgetShareReceived[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareEmail, setShareEmail] = useState('');
  const [creatingShare, setCreatingShare] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [owned, received] = await Promise.all([
        sharingApi.getSharesOwned(),
        sharingApi.getSharesReceived(),
      ]);
      setSharesOwned(owned);
      setSharesReceived(received);
    } catch (err) {
      console.error('Error fetching budget shares:', err);
      setError('Failed to load sharing settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateShare = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = shareEmail.trim().toLowerCase();
    if (!email) return;

    // Basic email validation
    if (!email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setCreatingShare(true);
      setError(null);
      await sharingApi.createShare(email);
      setSuccessMessage(`Budget shared with ${email}`);
      setShareEmail('');
      // Refresh the list
      const owned = await sharingApi.getSharesOwned();
      setSharesOwned(owned);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Error creating share:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to share budget';
      // Check for specific error messages from API
      if (errorMessage.includes('already shared')) {
        setError('You have already shared your budget with this user');
      } else if (errorMessage.includes('not found')) {
        setError('No user found with that email address');
      } else if (errorMessage.includes('yourself')) {
        setError('You cannot share your budget with yourself');
      } else {
        setError(errorMessage);
      }
    } finally {
      setCreatingShare(false);
    }
  };

  const handleRevokeShare = async (share: BudgetShareOwned) => {
    if (!window.confirm(`Are you sure you want to revoke ${share.sharedWithUser.displayName || share.sharedWithUser.email}'s access to your budget?`)) {
      return;
    }

    try {
      setError(null);
      await sharingApi.revokeShare(share.id);
      // Refresh the list
      const owned = await sharingApi.getSharesOwned();
      setSharesOwned(owned);
    } catch (err) {
      console.error('Error revoking share:', err);
      setError('Failed to revoke share');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className={embedded ? styles.embeddedContainer : styles.container}>
        {!embedded && <h2 className={styles.title}>Budget Sharing</h2>}
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={embedded ? styles.embeddedContainer : styles.container}>
      {!embedded && (
        <>
          <h2 className={styles.title}>Budget Sharing</h2>
          <p className={styles.description}>
            Share your budget data with other users in read-only mode.
          </p>
        </>
      )}

      {error && <div className={styles.error}>{error}</div>}
      {successMessage && <div className={styles.success}>{successMessage}</div>}

      {/* Share with someone */}
      <SettingsSection
        title="Share Your Budget"
        description="Give someone read-only access to view your budget data"
      >
        <form onSubmit={handleCreateShare} className={styles.createForm}>
          <input
            type="email"
            placeholder="Enter email address"
            value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            className={styles.input}
          />
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={creatingShare}
            disabled={!shareEmail.trim()}
          >
            Share
          </Button>
        </form>
      </SettingsSection>

      {/* People you've shared with */}
      <SettingsSection
        title="People With Access"
        description="These users can view your budget in read-only mode"
      >
        <SettingsList emptyMessage="You haven't shared your budget with anyone">
          {sharesOwned.map((share) => (
            <SettingsListItem key={share.id}>
              <div className={styles.shareInfo}>
                <div className={styles.shareName}>
                  {share.sharedWithUser.displayName || share.sharedWithUser.email}
                </div>
                {share.sharedWithUser.displayName && (
                  <div className={styles.shareEmail}>{share.sharedWithUser.email}</div>
                )}
                <div className={styles.shareMeta}>
                  Shared on {formatDate(share.createdAt)}
                </div>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleRevokeShare(share)}
              >
                Revoke
              </Button>
            </SettingsListItem>
          ))}
        </SettingsList>
      </SettingsSection>

      {/* Budgets shared with you */}
      <SettingsSection
        title="Shared With You"
        description="Budgets other users have shared with you"
      >
        <SettingsList emptyMessage="No one has shared their budget with you">
          {sharesReceived.map((share) => (
            <SettingsListItem key={share.id}>
              <div className={styles.shareInfo}>
                <div className={styles.shareName}>
                  {share.owner.displayName || share.owner.email}
                </div>
                {share.owner.displayName && (
                  <div className={styles.shareEmail}>{share.owner.email}</div>
                )}
                <div className={styles.shareMeta}>
                  <span className={styles.readOnlyBadge}>Read-only</span>
                  {' • '}Shared on {formatDate(share.createdAt)}
                </div>
              </div>
            </SettingsListItem>
          ))}
        </SettingsList>
      </SettingsSection>
    </div>
  );
};

export default BudgetShareSettings;
